import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";
import { Award } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import DateFilter from "./layout/DateFilter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const LEVEL_COLORS = { L0: "#34D399", L1: "#00D4FF", L2: "#FBBF24", L3: "#F472B6", Others: "#64748B" };
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
const tip = { background: "#0A0B10", border: "1px solid #2A2D3A", fontSize: 12 };

export default function GfrcFpyLevelsCard({ showMexico }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [view, setView] = useState("bar");
  const [metric, setMetric] = useState("pieces");
  const [data, setData] = useState(null);
  const [openLevel, setOpenLevel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    api.get("/departments/GFRC/fpy-levels", { params: p })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    return () => { cancelled = true; };
  }, [filter, showMexico]);

  const levels = data?.levels || [];
  const open = levels.find((l) => l.level === openLevel);

  return (
    <section className="relative bg-[#12141D] border border-border p-5" data-testid="gfrc-fpy-card">
      <span className="absolute top-0 left-0 right-0 h-[2px] bg-[#F472B6]" />
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading font-bold text-xl flex items-center gap-2">
            <Award size={18} className="text-[#F472B6]" /> First Pass Yield · Quality Levels
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            QC: First Pass Yield · PASS pieces by level (from Comments), by Checklist Date · {showMexico ? "MEXICO" : "SLC"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Toggle testid="gfrc-fpy-view" value={view} onChange={setView} options={[["bar", "Bar"], ["pie", "Pie"]]} />
          {view === "pie" && <Toggle testid="gfrc-fpy-metric" value={metric} onChange={setMetric} options={[["pieces", "Pieces"], ["sf", "SQFT"]]} />}
        </div>
      </div>
      <div className="mb-4"><DateFilter value={filter} onChange={setFilter} /></div>
      <LevelTiles data={data} onPick={setOpenLevel} />
      {data && data.total_pieces === 0 && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground" data-testid="gfrc-fpy-empty">No First Pass Yield pieces in this range</div>
      )}
      {data && data.total_pieces > 0 && (
        <div className="h-[300px]" data-testid="gfrc-fpy-chart">
          {view === "bar" ? <LevelBar levels={levels} onPick={setOpenLevel} /> : <LevelPie levels={levels} metric={metric} onPick={setOpenLevel} />}
        </div>
      )}
      <PiecesDialog level={open} onClose={() => setOpenLevel(null)} />
    </section>
  );
}

const Toggle = ({ testid, value, onChange, options }) => (
  <div className="inline-flex border border-border" data-testid={testid}>
    {options.map(([k, label]) => (
      <button key={k} data-testid={`${testid}-${k}`} onClick={() => onChange(k)}
        className={`px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors ${value === k ? "bg-[#F472B6] text-black font-semibold" : "text-muted-foreground hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

const LevelTiles = ({ data, onPick }) => (
  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
    <div className="bg-[#0A0B10] border border-border p-3" data-testid="gfrc-fpy-total">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
      <div className="font-mono font-semibold text-lg tabular-nums mt-1">{fmt(data?.total_pieces)} pcs</div>
      <div className="font-mono text-xs text-muted-foreground">{fmt(data?.total_sf)} SF</div>
    </div>
    {(data?.levels || []).map((l) => (
      <button key={l.level} onClick={() => l.pieces && onPick(l.level)} data-testid={`gfrc-fpy-tile-${l.level}`}
        className="text-left bg-[#0A0B10] border border-border p-3 hover:border-white/30 transition-colors relative">
        <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: LEVEL_COLORS[l.level] }} />
        <div className="text-[10px] uppercase tracking-widest" style={{ color: LEVEL_COLORS[l.level] }}>{l.level}</div>
        <div className="font-mono font-semibold text-lg tabular-nums mt-1">{fmt(l.pieces)} pcs <span className="text-xs text-muted-foreground">{l.pieces_pct}%</span></div>
        <div className="font-mono text-xs text-muted-foreground">{fmt(l.sf)} SF · {l.sf_pct}%</div>
      </button>
    ))}
  </div>
);

const LevelBar = ({ levels, onPick }) => (
  <ResponsiveContainer width="100%" height={300} minWidth={200}>
    <BarChart data={levels} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid stroke="#1F2230" vertical={false} />
      <XAxis dataKey="level" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
      <YAxis yAxisId="p" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
      <YAxis yAxisId="s" orientation="right" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
      <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={tip} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar yAxisId="p" dataKey="pieces" name="Pieces" cursor="pointer" onClick={(d) => onPick(d.level)}>
        {levels.map((l) => <Cell key={l.level} fill={LEVEL_COLORS[l.level]} />)}
      </Bar>
      <Bar yAxisId="s" dataKey="sf" name="SQFT" cursor="pointer" onClick={(d) => onPick(d.level)}>
        {levels.map((l) => <Cell key={l.level} fill={LEVEL_COLORS[l.level]} fillOpacity={0.4} />)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const LevelPie = ({ levels, metric, onPick }) => {
  const shown = useMemo(() => levels.filter((l) => l[metric] > 0), [levels, metric]);
  return (
    <ResponsiveContainer width="100%" height={300} minWidth={200}>
      <PieChart>
        <Pie data={shown} dataKey={metric} nameKey="level" innerRadius={60} outerRadius={110} paddingAngle={2}
          cursor="pointer" onClick={(d) => onPick(d.level)}
          label={({ level, value }) => `${level}: ${fmt(value)}`}>
          {shown.map((l) => <Cell key={l.level} fill={LEVEL_COLORS[l.level]} stroke="#12141D" />)}
        </Pie>
        <Tooltip contentStyle={tip} formatter={(v) => [fmt(v), metric === "sf" ? "SQFT" : "Pieces"]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const PiecesDialog = ({ level, onClose }) => (
  <Dialog open={!!level} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-4xl bg-[#12141D] border-border" data-testid="gfrc-fpy-dialog">
      <DialogHeader>
        <DialogTitle style={{ color: LEVEL_COLORS[level?.level] }}>
          {level?.level} · {fmt(level?.pieces)} pieces · {fmt(level?.sf)} SF
        </DialogTitle>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              {["Job", "Piece Mark", "S/N", "Plant", "SF", "Checklist Date", "Comment"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
            </tr>
          </thead>
          <tbody data-testid="gfrc-fpy-dialog-body">
            {(level?.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-border/40" data-testid={`gfrc-fpy-row-${i}`}>
                <td className="py-2 pr-3">{r.job}</td>
                <td className="py-2 pr-3 font-mono">{r.piece_mark || "—"}</td>
                <td className="py-2 pr-3 font-mono">{r.piece_sn || "—"}</td>
                <td className="py-2 pr-3 text-muted-foreground">{r.plant}</td>
                <td className="py-2 pr-3 font-mono text-right">{fmt(r.total_sf)}</td>
                <td className="py-2 pr-3 font-mono">{(r.check_date || "").slice(0, 10) || "—"}</td>
                <td className="py-2 pr-3 text-muted-foreground whitespace-pre-line">{r.comments || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DialogContent>
  </Dialog>
);
